import React, { useState, useEffect } from 'react';

// ハンバーガーメニューコンポーネント
const HamburgerMenu = ({ isOpen, onToggle, children, isDark = false }) => {
    return (
        <div className="relative">
            {/* ハンバーガーボタン */}
            <button
                type="button"
                onClick={onToggle}
                className="md:hidden flex flex-col justify-center items-center w-8 h-8 space-y-1.5 focus:outline-none"
                aria-label="メニューを開く"
            >
                <span className={`block w-6 h-0.5 ${isDark ? 'bg-gray-300' : 'bg-gray-600'} transition-all duration-300 ${isOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
                <span className={`block w-6 h-0.5 ${isDark ? 'bg-gray-300' : 'bg-gray-600'} transition-all duration-300 ${isOpen ? 'opacity-0' : ''}`}></span>
                <span className={`block w-6 h-0.5 ${isDark ? 'bg-gray-300' : 'bg-gray-600'} transition-all duration-300 ${isOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
            </button>
            
            {/* メニューコンテンツ */}
            <div className={`absolute right-0 top-full mt-2 w-72 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg shadow-lg border transition-all duration-300 transform ${isOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'} md:hidden z-50`}>
                <div className="py-2">
                    {children}
                </div>
            </div>
        </div>
    );
};

// 個人向けランディングページコンポーネント
const PersonalLandingPage = ({ onSwitchPage }) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [canScrollDown, setCanScrollDown] = useState(true);

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    useEffect(() => {
        const checkScrollable = () => {
            const windowHeight = window.innerHeight;
            const documentHeight = document.documentElement.scrollHeight;
            const scrollTop = window.scrollY || document.documentElement.scrollTop;
            const scrollBottom = scrollTop + windowHeight;
            
            // まだ下にスクロール可能かどうかを判定（10pxのマージンを持たせる）
            const isScrollable = scrollBottom < documentHeight - 10;
            setCanScrollDown(isScrollable);
        };

        // 初回チェック
        checkScrollable();

        // スクロールイベントとリサイズイベントを監視
        window.addEventListener('scroll', checkScrollable);
        window.addEventListener('resize', checkScrollable);

        return () => {
            window.removeEventListener('scroll', checkScrollable);
            window.removeEventListener('resize', checkScrollable);
        };
    }, []);

    return (
        <div className="min-h-screen bg-blue-50 text-gray-800 antialiased">
            {/* ヘッダー */}
            <header className="bg-white shadow-sm py-4 sticky top-0 z-10">
                <div className="w-full px-4 flex justify-between items-center">
                    <h1 className="text-2xl font-bold text-teal-600 rounded-lg flex items-center gap-2">
                        <img src="/favicon-32x32.png" alt="KOKONO-CVアイコン" className="hidden md:inline-block w-6 h-6 md:w-8 md:h-8" />
                        履歴書ツクール <span className="text-[0.8em] md:text-[0.8em]">KOKONO-CV</span>
                    </h1>
                    
                    {/* デスクトップ用ナビゲーション */}
                    <nav className="hidden md:flex space-x-4 items-center">
                        <a href="#goodbye-disposable" className="text-gray-600 hover:text-teal-600 px-3 py-2 rounded-md transition-colors duration-200">使い捨てにさようなら</a>
                        <a href="#features" className="text-gray-600 hover:text-teal-600 px-3 py-2 rounded-md transition-colors duration-200">機能</a>
                        <a href="#benefits" className="text-gray-600 hover:text-teal-600 px-3 py-2 rounded-md transition-colors duration-200">利用シーン</a>
                        <button type="button" onClick={() => onSwitchPage('corporate')} className="text-blue-600 hover:text-blue-800 px-3 py-2 rounded-md transition-colors duration-200 border border-blue-200 hover:border-blue-400">法人向けページ</button>
                        <a href="https://cvapp.kokono.cloud" className="bg-teal-500 text-white px-4 py-2 rounded-full shadow-md hover:bg-teal-600 transition-colors duration-200 ml-4">今すぐ無料で始める</a>
                    </nav>

                    {/* モバイル用ハンバーガーメニュー */}
                    <HamburgerMenu isOpen={isMenuOpen} onToggle={toggleMenu}>
                        <button type="button" onClick={() => { document.getElementById('goodbye-disposable')?.scrollIntoView({ behavior: 'smooth' }); toggleMenu(); }} className="block w-full text-left px-4 py-3 text-gray-600 hover:text-teal-600 hover:bg-teal-50 transition-colors duration-200">使い捨てにさようなら</button>
                        <button type="button" onClick={() => { document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' }); toggleMenu(); }} className="block w-full text-left px-4 py-3 text-gray-600 hover:text-teal-600 hover:bg-teal-50 transition-colors duration-200">機能</button>
                        <button type="button" onClick={() => { document.getElementById('benefits')?.scrollIntoView({ behavior: 'smooth' }); toggleMenu(); }} className="block w-full text-left px-4 py-3 text-gray-600 hover:text-teal-600 hover:bg-teal-50 transition-colors duration-200">利用シーン</button>
                        <button type="button" onClick={() => { onSwitchPage('corporate'); toggleMenu(); }} className="block w-full text-left px-4 py-3 text-blue-600 hover:text-blue-800 hover:bg-blue-50 transition-colors duration-200">法人向けページ</button>
                        <a href="https://cvapp.kokono.cloud" className="block px-4 py-3 bg-teal-500 text-white hover:bg-teal-600 transition-colors duration-200 mx-4 rounded-md text-center" onClick={toggleMenu}>今すぐ無料で始める</a>
                    </HamburgerMenu>
                </div>
            </header>

            {/* ヒーローセクション */}
            <section className="bg-gradient-to-r from-blue-300 to-teal-400 text-white py-20 md:py-32 text-center rounded-b-3xl mx-2 mt-4 shadow-xl">
                <div className="container mx-auto px-4">
                    <p className="text-xl md:text-2xl font-bold mb-4 bg-white bg-opacity-20 inline-block px-4 py-2 rounded-full shadow-md">
                        AIがサポート、簡単作成
                    </p>
                    <h2 className="text-2xl xs:text-3xl sm:text-4xl md:text-6xl font-extrabold mb-6 leading-tight">
                        <span className="block">あなたの可能性を最大限に。</span>
                        <span className="block">CVで、キャリアを変える。</span>
                    </h2>
                    <p className="text-lg md:text-xl mb-10 max-w-2xl mx-auto opacity-90">
                        無料で使えるCV作成Webサービス「履歴書ツクール KOKONO-CV」は、
                        あなたのスキル、経験、情熱を余すことなく伝え、次のステージへと導きます。
                    </p>
                    <a href="https://cvapp.kokono.cloud" className="bg-white text-teal-600 font-bold text-xl px-8 py-4 rounded-full shadow-lg hover:bg-blue-50 transform hover:scale-105 transition-all duration-300">
                        無料でKOKONO-CVを始める
                    </a>
                    {/* デスクワークの抽象的なイラスト */}
                    <div className="mt-12 flex justify-center relative -mx-4 px-4">
                        <img src="/img/desk800x400_1.png" alt="抽象的なデスクワークのイラスト" className="rounded-xl shadow-2xl max-w-full h-auto" />
                        <a href="https://cvapp.kokono.cloud" target="_blank" rel="noopener noreferrer">
                        <img src="/img/resume-edit.jpg" alt="履歴書編集" className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[130px] md:h-[300px] rounded-xl border-4 border-gray-200 hover:border-amber-300 shadow-[0_0_20px_rgba(251,191,36,0.4),0_0_40px_rgba(251,191,36,0.3)] hover:shadow-[0_0_20px_rgba(251,191,36,0.6),0_0_40px_rgba(251,191,36,0.45)] transition-all duration-300" />
                        <p className="absolute top-[calc(50%+30px)] md:top-[calc(50%+100px)] left-1/2 -translate-x-1/2 text-[#333] text-2xl md:text-3xl font-bold drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)] animate-pulse whitespace-nowrap">クリックでスタート！</p>
                        </a>
                    </div>
                </div>
            </section>

            {/* 使い捨てにさようならセクション */}
            <section id="goodbye-disposable" className="py-16 md:py-24 bg-teal-50 rounded-b-3xl mx-2 mt-4 shadow-xl text-center">
                <div className="container mx-auto px-4">
                    <h3 className="text-3xl md:text-4xl font-bold mb-6 text-gray-800">使い捨てにさようなら</h3>
                    <p className="text-lg md:text-xl mb-10 max-w-2xl mx-auto text-gray-600">
                        もう使い捨ての履歴書に時間を費やす必要はありません。KOKONO-CVで、あなたのキャリアを記録し続けましょう。
                    </p>
                    <div className="flex justify-center">
                        <img src="/img/trashResume480x380.png" alt="ゴミ箱の絵" className="rounded-xl shadow-2xl max-w-full h-auto" />
                    </div>
                </div>
            </section>

            {/* 機能セクション */}
            <section id="features" className="py-16 md:py-24 bg-white rounded-t-3xl mx-2 mt-4 shadow-xl">
                <div className="container mx-auto px-4">
                    <h3 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-800">KOKONO-CVが提供する価値</h3>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">


                        {/* 機能カード4: AIによる文章アシスト */}
                        <div className="bg-blue-50 p-8 rounded-xl shadow-lg border border-blue-100 hover:shadow-xl transition-shadow duration-300 flex flex-col items-center text-center">
                            <img src="/img/robotTalk5.png" alt="AIが文章作成をサポートするイメージ" className="rounded-md mb-4 max-w-full h-auto" />
                            <h4 className="text-xl font-semibold text-gray-800 mb-2">AIによる文章アシスト</h4>
                            <p className="text-gray-600">
                                <strong>志望動機や自己PR</strong>など、作成が難しいと感じる文章もAIがサポート。あなたの魅力を最大限に引き出す表現を見つけます。
                            </p>
                        </div>


                        {/* 機能カード1: あらゆるフォーマットに対応 */}
                        <div className="bg-blue-50 p-8 rounded-xl shadow-lg border border-blue-100 hover:shadow-xl transition-shadow duration-300 flex flex-col items-center text-center">
                            <img src="/img/resumes256x180.png" alt="多様なフォーマットのドキュメント作成のイメージ" className="rounded-md mb-4 max-w-full h-auto" />
                            <h4 className="text-xl font-semibold text-gray-800 mb-2">あらゆるフォーマットに対応</h4>
                            <p className="text-gray-600">
                                たった一度の入力で、<strong>履歴書、英語圏向けRESUME、厚生労働省準拠の職務経歴書</strong>を瞬時に作成。グローバルな転職も国内転職もこれ一つで安心です。
                            </p>
                        </div>

                        {/* 機能カード2: あなたの成長を記録 */}
                        <div className="bg-blue-50 p-8 rounded-xl shadow-lg border border-blue-100 hover:shadow-xl transition-shadow duration-300 flex flex-col items-center text-center">
                            <img src="/img/listCircle256x180.png" alt="成長を記録するイメージ" className="rounded-md mb-4 max-w-full h-auto" />
                            <h4 className="text-xl font-semibold text-gray-800 mb-2">あなたの成長を記録</h4>
                            <p className="text-gray-600">
                                継続的に追記できる設計だから、職務経歴や学習記録を常に最新の状態に。あなたのキャリアの物語を紡ぎ続けられます。
                            </p>
                        </div>

                        {/* 機能カード3: スキルを簡単に可視化 */}
                        <div className="bg-blue-50 p-8 rounded-xl shadow-lg border border-blue-100 hover:shadow-xl transition-shadow duration-300 flex flex-col items-center text-center">
                            <img src="/img/industries.png" alt="スキルを可視化するイメージ" className="rounded-md mb-4 max-w-full h-auto" />
                            <h4 className="text-xl font-semibold text-gray-800 mb-2">スキルを簡単に可視化</h4>
                            <p className="text-gray-600">
                                <strong>13職種のスキルセット</strong>から選択し、経験年数を入力するだけであなたの専門性を明確にアピール。スキルマッチングも容易に。
                            </p>
                        </div>
                        {/* 機能カード5: AIキャリアコンサル */}
                        <div className="bg-blue-50 p-8 rounded-xl shadow-lg border border-blue-100 hover:shadow-xl transition-shadow duration-300 flex flex-col items-center text-center">
                            <img src="/img/robotTalk3.png" alt="AIがキャリアコンサルを行うイメージ" className="rounded-md mb-4 max-w-full h-auto" />
                            <h4 className="text-xl font-semibold text-gray-800 mb-2">AIキャリアコンサル</h4>
                            <p className="text-gray-600">
                                自己紹介や推薦状など、AIがあなたのパーソナリティを深く理解し、状況に応じた最適なメッセージを生成します。
                            </p>
                        </div>

                        {/* 機能カード6: 価値を余すことなく記録 */}
                        <div className="bg-blue-50 p-8 rounded-xl shadow-lg border border-blue-100 hover:shadow-xl transition-shadow duration-300 flex flex-col items-center text-center">
                            <img src="/img/UI256x180.png" alt="価値を記録するイメージ" className="rounded-md mb-4 max-w-full h-auto" />
                            <h4 className="text-xl font-semibold text-gray-800 mb-2">価値を余すことなく記録</h4>
                            <p className="text-gray-600">
                                あなたの持つ全ての経験、スキル、そして達成を詳細に記録。業界の様々なスキルが候補で表示されるアシストUIで楽々入力。
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* 利用シーンセクション */}
            <section id="benefits" className="py-16 md:py-24 bg-teal-50 rounded-b-3xl mx-2 shadow-xl">
                <div className="container mx-auto px-4">
                    <h3 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-800">KOKONO-CVで広がるあなたの可能性</h3>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {/* キャリア形成・転職活動 */}
                        <div className="bg-white p-8 rounded-xl shadow-lg border border-teal-100 hover:shadow-xl transition-shadow duration-300 flex flex-col items-center text-center">
                            <h4 className="text-2xl font-semibold mb-4 text-teal-600">キャリア形成・転職活動</h4>
                            <p className="text-gray-700 leading-relaxed">
                                履歴書作成はもちろん、日々のワークヒストリー記録や、リスキリングの学習成果をまとめて。あなたのキャリアパスを強力にサポートします。
                            </p>
                            <img src="/img/use1_256x180.png" alt="作業按分を設定" className="rounded-md mb-4 max-w-full h-auto" />
                        </div>

                        {/* 自己分析・目標設定 */}
                        <div className="bg-white p-8 rounded-xl shadow-lg border border-teal-100 hover:shadow-xl transition-shadow duration-300 flex flex-col items-center text-center">
                            <h4 className="text-2xl font-semibold mb-4 text-teal-600">自己分析・成長の可視化</h4>
                            <p className="text-gray-700 leading-relaxed">
                                経験やスキルを詳細に記録することで、自身の強みや弱みを客観的に把握。次のキャリア目標設定に役立てることができます。
                            </p>
                            <img src="/img/use2_256x180.png" alt="作業按分を設定" className="rounded-md mb-4 max-w-full h-auto" />
                        </div>

                        {/* 副業・フリーランス活動 */}
                        <div className="bg-white p-8 rounded-xl shadow-lg border border-teal-100 hover:shadow-xl transition-shadow duration-300 flex flex-col items-center text-center">
                            <h4 className="text-2xl font-semibold mb-4 text-teal-600">副業・フリーランスのポートフォリオ</h4>
                            <p className="text-gray-700 leading-relaxed">
                                個人事業主やフリーランスの方も、KOKONO-CVでこれまでの実績やスキルをプロフェッショナルにまとめ、新しい仕事の獲得に繋げられます。
                            </p>
                            <img src="/img/use3_256x180.png" alt="作業按分を設定" className="rounded-md mb-4 max-w-full h-auto" />
                        </div>
                    </div>
                </div>
            </section>

            {/* キャリアを築くセクション (面接イラスト) */}
            <section className="py-16 md:py-24 bg-white rounded-t-3xl mx-2 mt-4 shadow-xl text-center">
                <div className="container mx-auto px-4">
                    <h3 className="text-3xl md:text-4xl font-bold mb-6 text-gray-800">自信を持って、次の扉を開こう</h3>
                    <p className="text-lg md:text-xl mb-10 max-w-2xl mx-auto text-gray-600">
                        私たちのAIコンサル機能が、面接での自己アピールや紹介状作成を強力に支援します。
                        KOKONO-CVで、あなたの「なりたい」を現実にしましょう。
                    </p>
                    {/* 面接風景の抽象的なイラスト */}
                    <div className="flex justify-center">
                        <img src="/img/motoGirl.png" alt="抽象的な面接風景のイラスト" className="rounded-xl shadow-2xl max-w-full h-auto" />
                    </div>
                </div>
            </section>

            {/* CTAセクション */}
            <section className="bg-gradient-to-r from-blue-400 to-teal-500 text-white py-16 md:py-20 text-center rounded-t-3xl mx-2 mb-4 shadow-xl">
                <div className="container mx-auto px-4">
                    <h3 className="text-3xl md:text-5xl font-extrabold mb-6">
                        あなたのキャリアを、今、加速させよう。
                    </h3>
                    <p className="text-lg md:text-xl mb-10 opacity-90">
                        無料でKOKONO-CVを始めて、あなたの可能性を最大限に引き出しましょう。
                    </p>
                    <a href="https://cvapp.kokono.cloud" className="bg-white text-teal-600 font-bold text-xl px-10 py-5 rounded-full shadow-lg hover:bg-blue-50 transform hover:scale-105 transition-all duration-300">
                        無料でアカウントを作成
                    </a>
                </div>
            </section>

            {/* フッター */}
            <footer className="bg-gray-800 text-gray-400 py-8">
                <div className="container mx-auto px-4 text-center">
                    <p>&copy; 2026 履歴書ツクール KOKONO-CV. All rights reserved.</p>
                    <div className="mt-4">
                        <a href="https://cvapp.kokono.cloud/privacy-policy" className="text-gray-400 hover:text-white mx-2">プライバシーポリシー</a>
                        <span className="text-gray-600">|</span>
                        <a href="https://cvapp.kokono.cloud/terms-of-service" className="text-gray-400 hover:text-white mx-2">利用規約</a>
                    </div>
                </div>
            </footer>

            {/* スクロール可能を示す下向き三角形アニメーション */}
            {canScrollDown && (
                <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50 pointer-events-none">
                    <div className="animate-bounce">
                        <svg 
                            className="w-8 h-8 text-gray-600 opacity-70" 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                            aria-label="下にスクロール可能"
                        >
                            <title>下にスクロール可能</title>
                            <path 
                                strokeLinecap="round" 
                                strokeLinejoin="round" 
                                strokeWidth={2} 
                                d="M19 14l-7 7m0 0l-7-7m7 7V3" 
                            />
                        </svg>
                    </div>
                </div>
            )}
        </div>
    );
};

// 法人向けお問い合わせページコンポーネント
const CorporateContactPage = ({ onSwitchPage }) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [formData, setFormData] = useState({
        companyName: '',
        contactPerson: '',
        email: '',
        phone: '',
        inquiryType: '',
        message: ''
    });

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        // ここでフォームデータを処理（例: API送信）
        console.log('お問い合わせフォームが送信されました:', formData);
        alert('お問い合わせありがとうございます。担当者よりご連絡いたします。'); // NOTE: 実際のアプリでは`alert`ではなく、モーダルUIを使用してください。
        // フォームをリセット
        setFormData({
            companyName: '',
            contactPerson: '',
            email: '',
            phone: '',
            inquiryType: '',
            message: ''
        });
    };

    return (
        <div className="min-h-screen bg-gray-100 text-gray-900 antialiased">
            {/* ヘッダー */}
            <header className="bg-gray-800 shadow-sm py-4 sticky top-0 z-10">
                <div className="w-full px-4 flex justify-between items-center">
                    <h1 className="text-2xl font-bold text-indigo-400 rounded-lg flex items-center gap-2">
                        <img src="/favicon-32x32.png" alt="KOKONO-CVアイコン" className="hidden md:inline-block w-6 h-6 md:w-8 md:h-8" />
                        履歴書ツクール <span className="text-[0.8em] md:text-[0.8em]">KOKONO-CV</span>
                    </h1>
                    
                    {/* デスクトップ用ナビゲーション */}
                    <nav className="hidden md:flex space-x-4 items-center">
                        <button type="button" onClick={() => onSwitchPage('corporate')} className="text-gray-300 hover:text-indigo-400 px-3 py-2 rounded-md transition-colors duration-200">法人向けトップへ</button>
                        <button type="button" onClick={() => onSwitchPage('personal')} className="text-blue-200 hover:text-blue-50 px-3 py-2 rounded-md transition-colors duration-200 border border-blue-400 hover:border-blue-200">個人向けページ</button>
                    </nav>

                    {/* モバイル用ハンバーガーメニュー */}
                    <HamburgerMenu isOpen={isMenuOpen} onToggle={toggleMenu} isDark={true}>
                        <button type="button" onClick={() => { onSwitchPage('corporate'); toggleMenu(); }} className="block w-full text-left px-4 py-3 text-gray-300 hover:text-indigo-400 hover:bg-gray-700 transition-colors duration-200">法人向けトップへ</button>
                        <button type="button" onClick={() => { onSwitchPage('personal'); toggleMenu(); }} className="block w-full text-left px-4 py-3 text-blue-200 hover:text-blue-50 hover:bg-gray-700 transition-colors duration-200">個人向けページ</button>
                    </HamburgerMenu>
                </div>
            </header>

            {/* お問い合わせフォームセクション */}
            <section className="py-16 md:py-24 bg-white rounded-t-3xl mx-2 mt-4 shadow-xl">
                <div className="container mx-auto px-4 max-w-3xl">
                    <h3 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-800">法人向けお問い合わせ</h3>
                    <p className="text-lg text-center text-gray-600 mb-8">
                        KOKONO-CV法人向けソリューションに関するご質問、資料請求など、お気軽にお問い合わせください。
                    </p>
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-1">貴社名 <span className="text-red-500">*</span></label>
                            <input
                                type="text"
                                id="companyName"
                                name="companyName"
                                value={formData.companyName}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200"
                            />
                        </div>
                        <div>
                            <label htmlFor="contactPerson" className="block text-sm font-medium text-gray-700 mb-1">ご担当者名 <span className="text-red-500">*</span></label>
                            <input
                                type="text"
                                id="contactPerson"
                                name="contactPerson"
                                value={formData.contactPerson}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200"
                            />
                        </div>
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">メールアドレス <span className="text-red-500">*</span></label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200"
                            />
                        </div>
                        <div>
                            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">電話番号</label>
                            <input
                                type="tel"
                                id="phone"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200"
                            />
                        </div>
                        <div>
                            <label htmlFor="inquiryType" className="block text-sm font-medium text-gray-700 mb-1">お問い合わせ種別 <span className="text-red-500">*</span></label>
                            <select
                                id="inquiryType"
                                name="inquiryType"
                                value={formData.inquiryType}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200"
                            >
                                <option value="">選択してください</option>
                                <option value="資料請求">資料請求</option>
                                <option value="サービス詳細">サービス詳細について</option>
                                <option value="導入相談">導入のご相談</option>
                            </select>
                        </div>
                        <div>
                            <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">お問い合わせ内容 <span className="text-red-500">*</span></label>
                            <textarea
                                id="message"
                                name="message"
                                value={formData.message}
                                onChange={handleChange}
                                required
                                rows="6"
                                className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200"
                            ></textarea>
                        </div>
                        <div className="text-center">
                            <button
                                type="submit"
                                className="bg-indigo-600 text-white font-bold text-lg px-8 py-3 rounded-full shadow-lg hover:bg-indigo-700 transform hover:scale-105 transition-all duration-300"
                            >
                                送信
                            </button>
                        </div>
                    </form>
                </div>
            </section>

            {/* フッター */}
            <footer className="bg-gray-900 text-gray-400 py-8">
                <div className="container mx-auto px-4 text-center">
                    <p>&copy; 2026 履歴書ツクール KOKONO-CV. All rights reserved.</p>
                    <div className="mt-4">
                        <a href="https://cvapp.kokono.cloud/privacy-policy" className="text-gray-400 hover:text-white mx-2">プライバシーポリシー</a>
                        <span className="text-gray-600">|</span>
                        <a href="https://cvapp.kokono.cloud/terms-of-service" className="text-gray-400 hover:text-white mx-2">利用規約</a>
                    </div>
                </div>
            </footer>
        </div>
    );
};

// 法人向けランディングページコンポーネント
const CorporateLandingPage = ({ onSwitchPage }) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [canScrollDown, setCanScrollDown] = useState(true);

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    useEffect(() => {
        const checkScrollable = () => {
            const windowHeight = window.innerHeight;
            const documentHeight = document.documentElement.scrollHeight;
            const scrollTop = window.scrollY || document.documentElement.scrollTop;
            const scrollBottom = scrollTop + windowHeight;
            
            // まだ下にスクロール可能かどうかを判定（10pxのマージンを持たせる）
            const isScrollable = scrollBottom < documentHeight - 10;
            setCanScrollDown(isScrollable);
        };

        // 初回チェック
        checkScrollable();

        // スクロールイベントとリサイズイベントを監視
        window.addEventListener('scroll', checkScrollable);
        window.addEventListener('resize', checkScrollable);

        return () => {
            window.removeEventListener('scroll', checkScrollable);
            window.removeEventListener('resize', checkScrollable);
        };
    }, []);

    return (
        <div className="min-h-screen bg-gray-100 text-gray-900 antialiased">
            {/* ヘッダー */}
            <header className="bg-gray-800 shadow-sm py-4 sticky top-0 z-10">
                <div className="w-full px-4 flex justify-between items-center">
                    <h1 className="text-2xl font-bold text-indigo-400 rounded-lg flex items-center gap-2">
                        <img src="/favicon-32x32.png" alt="KOKONO-CVアイコン" className="hidden md:inline-block w-6 h-6 md:w-8 md:h-8" />
                        履歴書ツクール <span className="text-[0.8em] md:text-[0.8em]">KOKONO-CV</span>
                    </h1>
                    
                    {/* デスクトップ用ナビゲーション */}
                    <nav className="hidden md:flex space-x-4 items-center">
                        <a href="#corporate-features" className="text-gray-300 hover:text-indigo-400 px-3 py-2 rounded-md transition-colors duration-200">機能</a>
                        <a href="#corporate-use-cases" className="text-gray-300 hover:text-indigo-400 px-3 py-2 rounded-md transition-colors duration-200">利用例</a>
                        <a href="#corporate-benefits" className="text-gray-300 hover:text-indigo-400 px-3 py-2 rounded-md transition-colors duration-200">利用メリット</a>
                        <button type="button" onClick={() => onSwitchPage('personal')} className="text-blue-200 hover:text-blue-50 px-3 py-2 rounded-md transition-colors duration-200 border border-blue-400 hover:border-blue-200">個人向けページ</button>
                        <button type="button" onClick={() => onSwitchPage('corporateContact')} className="bg-indigo-500 text-white px-4 py-2 rounded-full shadow-md hover:bg-indigo-600 transition-colors duration-200 ml-4">お問い合わせ</button>
                    </nav>

                    {/* モバイル用ハンバーガーメニュー */}
                    <HamburgerMenu isOpen={isMenuOpen} onToggle={toggleMenu} isDark={true}>
                        <button type="button" onClick={() => { document.getElementById('corporate-features')?.scrollIntoView({ behavior: 'smooth' }); toggleMenu(); }} className="block w-full text-left px-4 py-3 text-gray-300 hover:text-indigo-400 hover:bg-gray-700 transition-colors duration-200">機能</button>
                        <button type="button" onClick={() => { document.getElementById('corporate-use-cases')?.scrollIntoView({ behavior: 'smooth' }); toggleMenu(); }} className="block w-full text-left px-4 py-3 text-gray-300 hover:text-indigo-400 hover:bg-gray-700 transition-colors duration-200">利用例</button>
                        <button type="button" onClick={() => { document.getElementById('corporate-benefits')?.scrollIntoView({ behavior: 'smooth' }); toggleMenu(); }} className="block w-full text-left px-4 py-3 text-gray-300 hover:text-indigo-400 hover:bg-gray-700 transition-colors duration-200">利用メリット</button>
                        <button type="button" onClick={() => { onSwitchPage('personal'); toggleMenu(); }} className="block w-full text-left px-4 py-3 text-blue-200 hover:text-blue-50 hover:bg-gray-700 transition-colors duration-200">個人向けページ</button>
                        <button type="button" onClick={() => { onSwitchPage('corporateContact'); toggleMenu(); }} className="block w-full px-4 py-3 bg-indigo-500 text-white hover:bg-indigo-600 transition-colors duration-200 mx-4 rounded-md text-center">お問い合わせ</button>
                    </HamburgerMenu>
                </div>
            </header>

            {/* ヒーローセクション */}
            <section className="bg-gradient-to-r from-gray-700 to-gray-900 text-white py-20 md:py-32 text-center rounded-b-3xl mx-2 mt-4 shadow-xl">
                <div className="container mx-auto px-4">
                    <h2 className="text-4xl md:text-6xl font-extrabold mb-6 leading-tight">
                        企業の人材戦略を革新する。
                        <br />
                        KOKONO-CV 法人向けソリューション。
                    </h2>
                    <p className="text-lg md:text-xl mb-10 max-w-2xl mx-auto opacity-90">
                        人材の発掘から育成、最適な配置まで。KOKONO-CVは、貴社の採用活動と社内スキルマネジメントを強力に支援します。
                    </p>
                    <button type="button" onClick={() => onSwitchPage('corporateContact')} className="bg-indigo-500 text-white font-bold text-xl px-8 py-4 rounded-full shadow-lg hover:bg-indigo-600 transform hover:scale-105 transition-all duration-300">
                        資料請求・お問い合わせ
                    </button>
                    {/* チームコラボレーションの抽象的なイラスト */}
                    <div className="mt-12 flex justify-center">
                        <img src="/img/meeting800x420.png" alt="抽象的なチームコラボレーションのイラスト" className="rounded-xl shadow-2xl max-w-full h-auto" />
                    </div>
                </div>
            </section>

            {/* 機能セクション (法人向け) */}
            <section id="corporate-features" className="py-16 md:py-24 bg-white rounded-t-3xl mx-2 mt-4 shadow-xl">
                <div className="container mx-auto px-4">
                    <h3 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-800">KOKONO-CV 法人向け機能</h3>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {/* 機能カード1 (法人): 詳細な人材分析ダッシュボード */}
                        <div className="bg-gray-50 p-8 rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300 flex flex-col items-center text-center">
                            <img src="/img/graph256x180.png" alt="詳細な人材分析ダッシュボードのイメージ" className="rounded-md mb-4 max-w-full h-auto" />
                            <h4 className="text-xl font-semibold text-gray-800 mb-2">詳細な人材分析ダッシュボード</h4>
                            <p className="text-gray-600">
                                応募者や社員のスキル、経験、実績を網羅的に可視化。これまでにない詳細なデータ分析で、最適な人材配置や育成計画を立案できます。
                            </p>
                        </div>

                        {/* 機能カード2 (法人): 効率的な採用プロセス */}
                        <div className="bg-gray-50 p-8 rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300 flex flex-col items-center text-center">
                            <img src="/img/lineGraph256x180.png" alt="効率的な採用プロセスのイメージ" className="rounded-md mb-4 max-w-full h-auto" />
                            <h4 className="text-xl font-semibold text-gray-800 mb-2">効率的な採用プロセス</h4>
                            <p className="text-gray-600">
                                AIによる履歴書・職務経歴書の自動解析で、候補者のスクリーニング時間を大幅に短縮。採用担当者の負担を軽減し、効率的な採用を実現します。
                            </p>
                        </div>

                        {/* 機能カード3 (法人): 社内スキルマップ自動生成 */}
                        <div className="bg-gray-50 p-8 rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300 flex flex-col items-center text-center">
                            <img src="/img/listCircle256x180.png" alt="社内スキルマップ自動生成のイメージ" className="rounded-md mb-4 max-w-full h-auto" />
                            <h4 className="text-xl font-semibold text-gray-800 mb-2">社内スキルマップ自動生成</h4>
                            <p className="text-gray-600">
                                社員の登録データから、組織全体のスキルマップを自動で生成。必要なスキルを持つ人材を迅速に特定し、プロジェクトのアサインに役立てます。
                            </p>
                        </div>

                        {/* 機能カード4 (法人): 採用管理システム連携 */}
                        <div className="bg-gray-50 p-8 rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300 flex flex-col items-center text-center">
                            <img src="/img/learn256x180.png" alt="採用管理システム連携のイメージ" className="rounded-md mb-4 max-w-full h-auto" />
                            <h4 className="text-xl font-semibold text-gray-800 mb-2">ラーニングのサポート</h4>
                            <p className="text-gray-600">
                                社員の学習プランをAIがサポート。経験やスキルからAIが学習方針を提案します。
                            </p>
                        </div>

                        {/* 機能カード5 (法人): AIによる人材マッチング */}
                        <div className="bg-gray-50 p-8 rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300 flex flex-col items-center text-center">
                            <img src="/img/robotMeeting256x180.png" alt="AIによる人材マッチングのイメージ" className="rounded-md mb-4 max-w-full h-auto" />
                            <h4 className="text-xl font-semibold text-gray-800 mb-2">AIによる人材マッチング</h4>
                            <p className="text-gray-600">
                                求める人材像と候補者のスキル・経験をAIが分析し、最適なマッチングを提案。ミスマッチのリスクを低減し、採用の質を高めます。
                            </p>
                        </div>

                        {/* 管理人材・社員のデータ管理 */}
                        <div className="bg-gray-50 p-8 rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300 flex flex-col items-center text-center">
                            <img src="/img/path256x180.png" alt="セキュリティとプライバシー保護のイメージ" className="rounded-md mb-4 max-w-full h-auto" />
                            <h4 className="text-xl font-semibold text-gray-800 mb-2">キャリアパスの指針に</h4>
                            <p className="text-gray-600">
                                社員のキャリアパスをAIがサポート。経験やスキルからAIがキャリアパスを提案します。
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* 利用例セクション (法人向け) */}
            <section id="corporate-use-cases" className="py-16 md:py-24 bg-gray-50 rounded-b-3xl mx-2 shadow-xl">
                <div className="container mx-auto px-4">
                    <h3 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-800">KOKONO-CV 法人向け 利用例</h3>
                    <p className="text-lg text-center text-gray-600 mb-10 max-w-2xl mx-auto">
                        KOKONO-CVが、貴社の人材管理と業務効率化をどのようにサポートするか、具体的な業務フローをご紹介します。
                    </p>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {/* 利用例1: 社員が参加業務・スキルを入力 */}
                        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300 flex flex-col items-center text-center">
                            <div className="bg-indigo-100 p-3 rounded-full mb-4">
                                <span className="text-indigo-600 font-bold text-xl">1</span>
                            </div>
                            <img src="/img/N1_256x180.png" alt="社員がデータ入力" className="rounded-md mb-4 max-w-full h-auto" />
                            <h4 className="text-xl font-semibold text-gray-800 mb-2">社員が業務・スキルを入力</h4>
                            <p className="text-gray-600">
                                各社員が担当した業務内容、プロジェクトへの参加状況、習得したスキルを定期的にKOKONO-CVに入力します。
                            </p>
                        </div>

                        {/* 利用例2: 作業按分、管理・作業の割合など細かく設定可能 */}
                        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300 flex flex-col items-center text-center">
                            <div className="bg-indigo-100 p-3 rounded-full mb-4">
                                <span className="text-indigo-600 font-bold text-xl">2</span>
                            </div>
                            <img src="/img/N2_256x180.png" alt="作業按分を設定" className="rounded-md mb-4 max-w-full h-auto" />
                            <h4 className="text-xl font-semibold text-gray-800 mb-2">作業按分、割合を設定</h4>
                            <p className="text-gray-600">
                                各業務における作業按分、管理業務と実作業の割合など、詳細な設定が可能です。これによりより正確な計測が可能となります。
                            </p>
                        </div>

                        {/* 利用例3: 暦年の業務内容から経験値を計算 */}
                        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300 flex flex-col items-center text-center">
                            <div className="bg-indigo-100 p-3 rounded-full mb-4">
                                <span className="text-indigo-600 font-bold text-xl">3</span>
                            </div>
                            <img src="/img/N3_256x180.png" alt="経験値を計算" className="rounded-md mb-4 max-w-full h-auto" />
                            <h4 className="text-xl font-semibold text-gray-800 mb-2">業務内容から経験値を計算</h4>
                            <p className="text-gray-600">
                                スキル、業務内容,按分などの記録から、個々の社員の経験値や熟練度を自動で計算。客観的な評価指標を生成します。
                            </p>
                        </div>

                        {/* 利用例4: 作業アサインや経営意思決定資料に */}
                        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300 flex flex-col items-center text-center">
                            <div className="bg-indigo-100 p-3 rounded-full mb-4">
                                <span className="text-indigo-600 font-bold text-xl">4</span>
                            </div>
                            <img src="/img/bigMeetingRoom256x180.png" alt="意思決定に活用" className="rounded-md mb-4 max-w-full h-auto" />
                            <h4 className="text-xl font-semibold text-gray-800 mb-2">作業アサインや意思決定に活用</h4>
                            <p className="text-gray-600">
                                蓄積されたデータと経験値を基に、最適な人材をプロジェクトにアサイン。経営層の戦略的な意思決定にも貢献します。
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* 利用メリットセクション (法人向け) */}
            <section id="corporate-benefits" className="py-16 md:py-24 bg-blue-50 rounded-t-3xl mx-2 mt-4 shadow-xl">
                <div className="container mx-auto px-4">
                    <h3 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-800">KOKONO-CVがもたらす企業価値</h3>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {/* 人材発掘の加速 */}
                        <div className="bg-white p-8 rounded-xl shadow-lg border border-blue-100 hover:shadow-xl transition-shadow duration-300">
                            <h4 className="text-2xl font-semibold mb-4 text-blue-600">人材発掘の加速</h4>
                            <p className="text-gray-700 leading-relaxed">
                                潜在的な才能を持つ候補者を効率的に見つけ出し、採用競争力を高めます。貴社に最適な人材をスピーディーに確保。
                            </p>
                        </div>

                        {/* 社内リソースの最適化 */}
                        <div className="bg-white p-8 rounded-xl shadow-lg border border-blue-100 hover:shadow-xl transition-shadow duration-300">
                            <h4 className="text-2xl font-semibold mb-4 text-blue-600">社内リソースの最適化</h4>
                            <p className="text-gray-700 leading-relaxed">
                                社員のスキルと経験を正確に把握し、プロジェクトへの最適なアサインを支援。組織全体の生産性とエンゲージメントを向上させます。
                            </p>
                        </div>

                        {/* データ駆動型の人材戦略 */}
                        <div className="bg-white p-8 rounded-xl shadow-lg border border-blue-100 hover:shadow-xl transition-shadow duration-300">
                            <h4 className="text-2xl font-semibold mb-4 text-blue-600">データ駆動型の人材戦略</h4>
                            <p className="text-gray-700 leading-relaxed">
                                経験と勘に頼らない、データに基づいた客観的な意思決定を支援。戦略的な人材育成と組織開発を可能にします。
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTAセクション (法人向け) */}
            <section className="bg-gray-800 text-white py-16 md:py-20 text-center rounded-t-3xl mx-2 mb-4 shadow-xl">
                <div className="container mx-auto px-4">
                    <h3 className="text-3xl md:text-5xl font-extrabold mb-6">
                        貴社の採用と人材育成を、KOKONO-CVで最適化。
                    </h3>
                    <p className="text-lg md:text-xl mb-10 opacity-90">
                        貴社のビジネス成長に貢献する、新たな人材ソリューションをご体験ください。
                    </p>
                    <button type="button" onClick={() => onSwitchPage('corporateContact')} className="bg-indigo-500 text-white font-bold text-xl px-10 py-5 rounded-full shadow-lg hover:bg-indigo-600 transform hover:scale-105 transition-all duration-300">
                        資料請求・お問い合わせ
                    </button>
                </div>
            </section>

            {/* フッター (法人向け) */}
            <footer className="bg-gray-900 text-gray-400 py-8">
                <div className="container mx-auto px-4 text-center">
                    <p>&copy; 2026 履歴書ツクール KOKONO-CV. All rights reserved.</p>
                    <div className="mt-4">
                        <a href="https://cvapp.kokono.cloud/privacy-policy" className="text-gray-400 hover:text-white mx-2">プライバシーポリシー</a>
                        <span className="text-gray-600">|</span>
                        <a href="https://cvapp.kokono.cloud/terms-of-service" className="text-gray-400 hover:text-white mx-2">利用規約</a>
                    </div>
                </div>
            </footer>

            {/* スクロール可能を示す下向き三角形アニメーション */}
            {canScrollDown && (
                <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50 pointer-events-none">
                    <div className="animate-bounce">
                        <svg 
                            className="w-8 h-8 text-gray-300 opacity-70" 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                            aria-label="下にスクロール可能"
                        >
                            <title>下にスクロール可能</title>
                            <path 
                                strokeLinecap="round" 
                                strokeLinejoin="round" 
                                strokeWidth={2} 
                                d="M19 14l-7 7m0 0l-7-7m7 7V3" 
                            />
                        </svg>
                    </div>
                </div>
            )}
        </div>
    );
};

// メインのAppコンポーネント
const App = () => {
    const [currentPage, setCurrentPage] = useState('personal'); // 'personal', 'corporate', 'corporateContact'

    const handleSwitchPage = (page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' }); // ページ切り替え時にトップにスクロール
    };

    // 現在のページに基づいてコンポーネントをレンダリング
    switch (currentPage) {
        case 'personal':
            return <PersonalLandingPage key="personal" onSwitchPage={handleSwitchPage} />;
        case 'corporate':
            // CorporateLandingPageが確実に定義されていることを確認
            return <CorporateLandingPage key="corporate" onSwitchPage={handleSwitchPage} />;
        case 'corporateContact':
            return <CorporateContactPage key="corporateContact" onSwitchPage={handleSwitchPage} />;
        default:
            return <PersonalLandingPage key="personal-default" onSwitchPage={handleSwitchPage} />;
    }
};

export default App;
